import { fetchWithAuth } from '../AuthService';
import { DATA_SERVICE_BASE_URL } from '../ApiService';

/**
 * Events API service
 */
export const EventApi = {
  /**
   * Get all events
   * @returns {Promise<Response>} All events response
   */
  getEvents: async () => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/events`, {
      method: 'GET',
    });
  },

  /**
   * Get event by ID
   * @param {string|number} id - Event ID
   * @returns {Promise<Response>} Event response
   */
  getEvent: async (id) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/events/${id}`, {
      method: 'GET',
    });
  },

  /**
   * Create event
   * @param {Object} data - Event data
   * @returns {Promise<Response>} Create response
   */
  createEvent: async (data) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  /**
   * Update event
   * @param {string|number} id - Event ID
   * @param {Object} data - Updated event data
   * @returns {Promise<Object|null>} Updated event object, or null for 204 No Content
   * @throws {Error} On network errors or HTTP errors
   */
  updateEvent: async (id, data) => {
    console.log('[EventApi] PUT /events/:id', { id, payload: data });
    
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/events/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    // Handle 204 No Content (success with empty body)
    if (response.status === 204) {
      return null;
    }

    // Throw on HTTP errors
    if (!response.ok) {
      let errorMsg = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorBody = await response.json();
        errorMsg = errorBody.message || errorBody.error || errorMsg;
      } catch (e) {
        // Failed to parse error body, use default message
      }
      throw new Error(errorMsg);
    }

    // Parse response body
    const text = await response.text();
    if (!text) {
      // Empty body but 2xx status - treat as success with null
      return null;
    }

    try {
      return JSON.parse(text);
    } catch (parseError) {
      console.warn('Failed to parse event update response:', parseError);
      throw new Error('Invalid JSON response from server');
    }
  },

  /**
   * Mark event as completed
   * @param {string|number} id - Event ID
   * @returns {Promise<Response>} Mark completed response
   */
  markEventCompleted: async (id) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/events/${id}/complete`, {
      method: 'PATCH',
    });
  },

  /**
   * Delete event
   * @param {string|number} id - Event ID
   * @returns {Promise<Response>} Delete response
   */
  deleteEvent: async (id) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/events/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Delete entire recurring event series
   * @param {string|number} parentEventId - Parent event ID or any event ID in the series
   * @returns {Promise<Response>} Delete series response
   */
  deleteEventSeries: async (parentEventId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/events/${parentEventId}/series`, {
      method: 'DELETE',
    });
  },

  /**
   * Get events by endeavor
   * @param {string|number} endeavorId - Endeavor ID
   * @returns {Promise<Response>} Endeavor events response
   */
  getEventsByEndeavor: async (endeavorId) => {
    // Updated to use aggregated user events endpoint with endeavor filter
    const params = new URLSearchParams({
      endeavorId: String(endeavorId),
      page: '0',
      size: '10',
      sort: 'startDatetime,asc'
    }).toString();
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/events?${params}`, {
      method: 'GET'
    });
  },

  /**
   * Get events by date range
   * @param {string} start - Start date (ISO string)
   * @param {string} end - End date (ISO string)
   * @returns {Promise<Response>} Events in date range response
   */
  getEventsByDateRange: async (start, end) => {
    const queryParams = new URLSearchParams({ start, end }).toString();
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/events/date-range?${queryParams}`, {
      method: 'GET',
    });
  },

  /**
   * Get recurring events
   * @returns {Promise<Response>} Recurring events response
   */
  getRecurringEvents: async () => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/events/recurring`, {
      method: 'GET',
    });
  },

  /**
   * Get event metadata
   * @param {string|number} eventId - Event ID
   * @returns {Promise<Response>} Event metadata response
   */
  getEventMetadata: async (eventId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/events/${eventId}/metadata`, {
      method: 'GET',
    });
  },

  /**
   * Get filtered user events
   * @param {Object} options - Filter options
   * @returns {Promise<Response>} Filtered user events response
   */
  getUserEventsFiltered: async (options = {}) => {
    const queryParams = new URLSearchParams(options).toString();
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/events${queryParams ? '?' + queryParams : ''}`, {
      method: 'GET',
    });
  },

  /**
   * Generate series for a recurring event
   * @param {number} eventId - Event ID
   * @returns {Promise<Response>} Generate series response
   */
  generateEventSeries: async (eventId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/events/${eventId}/generate-series`, {
      method: 'POST',
    });
  },
};